import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Plus, User } from 'lucide-react';
import { useApp } from '../context/AppContext';
import Haptics from '../utils/haptics';

const BottomNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useApp();
  
  const isActive = (path: string) => location.pathname === path;

  const handlePlusClick = () => {
    Haptics.tap();
    const path = location.pathname;
    if (path === '/groups') navigate('/groups?action=new');
    else if (path === '/friends') navigate('/friends?action=add');
    else if (path.startsWith('/group/')) {
      const groupId = path.split('/').pop();
      navigate('/add-expense', { state: { groupId: groupId !== 'non-group' ? groupId : '' } });
    } else navigate('/add-expense');
  };

  const NavItem = ({ to, icon: Icon, label, testId }: { to: string, icon: any, label: string, testId?: string }) => (
    <Link 
      to={to} 
      data-testid={testId}
      onClick={() => Haptics.tap()}
      className={`flex flex-col items-center justify-center gap-1 transition-all duration-300 active:scale-95 h-full w-full ${isActive(to) ? 'text-brand-600 dark:text-brand-400' : 'text-gray-800 dark:text-gray-200'}`}
    >
      <Icon size={24} strokeWidth={isActive(to) ? 2.5 : 2} />
      <span className="text-[10px] font-black uppercase tracking-wider">{label}</span>
    </Link>
  );

  return (
    <nav className="border-t border-gray-100 dark:border-gray-800 w-full z-40 fixed bottom-0 max-w-md mx-auto pb-[env(safe-area-inset-bottom)] h-16 transition-colors duration-200">
      <div className="flex h-full items-center relative">
        {/* Left Half: Home Button Area */}
        <div className="flex-1 h-full flex items-center justify-center">
          <NavItem to="/dashboard" icon={Home} label={t('home')} testId="nav-dashboard" />
        </div>

        {/* Center: Absolute Positioned FAB to allow true 50/50 split of the bar */}
        <div className="absolute left-1/2 -translate-x-1/2 -top-6 pointer-events-none">
          <button 
              onClick={handlePlusClick}
              data-testid="nav-plus"
              className="bg-brand-600 dark:bg-brand-500 text-white rounded-2xl p-4 shadow-2xl shadow-brand-500/40 hover:shadow-brand-500/60 active:scale-90 transition-all duration-200 ring-4 ring-white dark:ring-gray-900 pointer-events-auto"
              aria-label={t('add_new')}
          >
            <Plus size={24} strokeWidth={3} />
          </button>
        </div>

        {/* Right Half: Account Button Area */}
        <div className="flex-1 h-full flex items-center justify-center">
          <NavItem to="/account" icon={User} label={t('account')} testId="nav-account" />
        </div>
      </div>
    </nav>
  );
};

export default BottomNav;
